import { t } from '@lingui/core/macro'

const baseAccountUrl = '/api/v1/account' as const

export const UrlAuditRoutes = {
  results: datasetId => `${baseAccountUrl}/url-audit/${datasetId}/results`,
  validateUrls: `${baseAccountUrl}/url-audit/validate-urls`,
} as const

export const ROUTES = {
  DATASET_SELECTIVE_EDIT: '/codes/details/:id/selective',
  DATASET_HISTORY: '/codes/details/:id/history',
  DATASET_DATA: '/codes/details/:id/data',
  DATASET_DATA_PROCESSING: '/codes/details/:id/data/processing',
  TEMPLATE_DATA: '/template/:id/data',
  TEMPLATE_PENDING: '/template/:id/pending',
  TEMPLATE_SELECTIVE: '/template/:id/selective',
  TEMPLATE_SUBMISSIONS: '/template/:id/submissions',
  TEMPLATE_INTEGRATIONS: '/template/:id/integrations',
  TEMPLATE_SYNC: '/template/:id/syncs',
  TEMPLATE_APPROVAL_REVIEW: '/template/:id/approval/:datasetId/review',
  TEMPLATE_APPROVAL: '/template/:id/approval',
  TEMPLATE_CAMPAIGN_HEALTH: '/template/:id/campaign-health',
  TEMPLATE_CAMPAIGN_HEALTH_SELECTIVE: '/template/:id/campaign-health/selective',
  ADMIN_TEMPLATE_EDIT_SETUP: '/admin/templates/edit-template/:id/setup',
  ADMIN_TEMPLATE_EDIT_GOVERN: '/admin/templates/edit-template/:id/govern',
  ADMIN_TEMPLATE_EDIT_CONNECT: '/admin/templates/edit-template/:id/connect',
  TAXONOMY_TRANSLATION: '/admin/taxonomy-translation',
  TAXONOMY_TRANSLATION_UPLOAD: '/admin/taxonomy-translation/create',
  TAXONOMY_TRANSLATION_DETAILS: '/admin/taxonomy-translation/:id',
} as const

export const ADMIN_ROUTES = {
  ACCOUNTS: '/admin/accounts',
  TARGETS: '/admin/targets',
  TARGETS_EDIT: '/admin/targets/:id',
  FIELDSET_FIELDS: '/admin/fieldsets/:id/fields',
} as const

export const DISABLED_HEADER_ROUTES = [ROUTES.TAXONOMY_TRANSLATION_UPLOAD] as const

export const BREADCRUMBS_BY_PATH = () => {
  const BREADCRUMB_PARENT = {
    INTEGRATIONS: { label: t`Integrations`, path: '' },
    ORGANIZATIONS: { label: t`Organizations`, path: '' },
    ELEMENTS: { label: t`Elements`, path: '' },
    TEMPLATES: { label: t`Templates`, path: '' },
  } as const

  return {
    ACCOUNTS: [BREADCRUMB_PARENT.INTEGRATIONS, { label: t`Accounts`, path: '/admin/accounts' }],
    TARGETS: [BREADCRUMB_PARENT.INTEGRATIONS, { label: t`Targets`, path: '/admin/targets' }],
    TARGETS_EDIT: [BREADCRUMB_PARENT.INTEGRATIONS, { label: t`Targets`, path: '/admin/targets' }],
  } as const
}

/**
 * Replace React Router path params with values.
 * @param {{}} args
 * @param {string} args.url React Router URL
 * @param {{ [key: string]: string }} args.params Params; { params: { id: "1" }} // Replaces `:id` with `1`
 * @param {{ [key: string]: string }} args.urlParams URL Params; { urlParams: { filter: "value" }} // Appends query string to URL
 * @example
 * routeByParam({
 *   url: ROUTES.DATASET_DATA,
 *   params: { id: originalDatasetId },
 *   urlParams: { search: "some_value" },
 * }) // '/codes/details/1/data?search=some_value'
 *
 */
export function routeByParam({
  url,
  params,
  urlParams = {},
}: {
  url: string
  params: Record<string, string>
  urlParams?: Record<string, string>
}) {
  if (!Object.keys(params).length)
    throw new Error(t`Params are required to replace parts of the URL.`)

  const parts = url.split('/')

  const processedUrl = parts
    .map(p => {
      const segmentParam = p.split(':')[1]
      if (segmentParam) return params[segmentParam]
      return p
    })
    .join('/')

  const queryString = new URLSearchParams(urlParams).toString()

  return queryString ? `${processedUrl}?${queryString}` : processedUrl
}
